import { isCalendarAvailable } from '../../../utils/calendar'
import { getSectionCanCreatePermission } from '../../../utils/common'
import { isStaffUser } from '../../../utils/user'
import { EMAIL_ADD_BUTTON_OPTIONS } from '../RequestProfileTabEmails'

import {
  REQUEST_PROFILE_HEADER_FOOTER_ACTION,
  REQUEST_PROFILE_HEADER_FOOTER_ACTION_ITEMS,
  REQUEST_PROFILE_HEADER_FOOTER_ACTION_PERMISSION,
  REQUEST_PROFILE_HEADER_FOOTER_ACTION_QA_NAME,
  REQUEST_PROFILE_HEADER_FOOTER_ACTION_SECTION,
  REQUEST_PROFILE_HEADER_FOOTER_ACTION_TEXT_KEY,
  REQUEST_PROFILE_HEADER_FOOTER_BAR_BORDERS_WIDTH,
  REQUEST_PROFILE_HEADER_FOOTER_DOC_FLOW_OPTION,
  REQUEST_PROFILE_HEADER_FOOTER_DOC_FLOW_OPTIONS,
} from './RequestProfileHeaderFooter.constants'

function getRequestProfileHeaderFooterActionOptions(action) {
  switch (action) {
    case REQUEST_PROFILE_HEADER_FOOTER_ACTION.docFlow:
      return REQUEST_PROFILE_HEADER_FOOTER_DOC_FLOW_OPTIONS
    case REQUEST_PROFILE_HEADER_FOOTER_ACTION.email:
      return EMAIL_ADD_BUTTON_OPTIONS
    default:
      return null
  }
}

function isRequestProfileHeaderFooterActionAppointment(action) {
  return action === REQUEST_PROFILE_HEADER_FOOTER_ACTION.appointment
}

export function isRequestProfileHeaderFooterActionEmail(action) {
  return action === REQUEST_PROFILE_HEADER_FOOTER_ACTION.email
}

export function isRequestProfileAppointmentAvailable({
  availableSections,
  outboundUuid,
  requestData,
  userData,
}) {
  return (
    isCalendarAvailable(availableSections) &&
    isStaffUser(userData) &&
    !outboundUuid &&
    !requestData?.isArchived
  )
}

export function isRequestProfileHeaderFooterDocFlowOptionConnect(value) {
  return value === REQUEST_PROFILE_HEADER_FOOTER_DOC_FLOW_OPTION.connect
}

export function isRequestProfileHeaderFooterActionPermitted({
  action,
  isAppointmentAvailable,
  requestData,
  sections,
}) {
  if (isRequestProfileHeaderFooterActionAppointment(action)) {
    return isAppointmentAvailable
  }

  const section = REQUEST_PROFILE_HEADER_FOOTER_ACTION_SECTION[action]

  if (section && !getSectionCanCreatePermission(sections, section)) {
    return false
  }

  return requestData?.permissions?.[
    REQUEST_PROFILE_HEADER_FOOTER_ACTION_PERMISSION[action]
  ]
}

export function getRequestProfileHeaderFooterActions({
  isAppointmentAvailable,
  requestData,
  sections,
  t,
}) {
  return REQUEST_PROFILE_HEADER_FOOTER_ACTION_ITEMS.filter(({ action }) =>
    isRequestProfileHeaderFooterActionPermitted({
      action,
      isAppointmentAvailable,
      requestData,
      sections,
    })
  ).map(item => ({
    ...item,
    options: getRequestProfileHeaderFooterActionOptions(item.action),
    qaName: REQUEST_PROFILE_HEADER_FOOTER_ACTION_QA_NAME[item.action],
    text: t(REQUEST_PROFILE_HEADER_FOOTER_ACTION_TEXT_KEY[item.action]),
  }))
}

export function getRequestProfileHeaderFooterMoreOptions(hiddenActions) {
  return hiddenActions.map(actionItem => ({
    label: actionItem.text,
    value: actionItem.action,
  }))
}

export function getRequestProfileHeaderFooterActionsVisibleCount({
  actionWidths,
  addNewTextWidth,
  containerWidth,
  moreButtonWidth,
}) {
  const totalActions = actionWidths.length

  const visibleActionsCountOptions = Array.from(
    { length: totalActions + 1 },
    (_, index) => totalActions - index
  )

  for (const visibleActionsCount of visibleActionsCountOptions) {
    const hiddenActionsCount = totalActions - visibleActionsCount
    const hasMoreButton = hiddenActionsCount > 0
    const visibleActionsWidth = actionWidths
      .slice(0, visibleActionsCount)
      .reduce((sum, currentWidth) => sum + currentWidth, 0)

    const totalWidth =
      REQUEST_PROFILE_HEADER_FOOTER_BAR_BORDERS_WIDTH +
      addNewTextWidth +
      visibleActionsWidth +
      (hasMoreButton ? moreButtonWidth : 0)

    if (totalWidth <= containerWidth) {
      return visibleActionsCount
    }
  }

  return 0
}

export function getRequestProfileHeaderFooterActionWidths({
  actionButtonRefs,
  actions,
  actionWidthsByKey,
}) {
  const nextActionWidthsByKey = {
    ...actionWidthsByKey,
  }

  const actionWidths = actions.map(action => {
    const measuredWidth = actionButtonRefs[action.action]?.offsetWidth

    if (measuredWidth) {
      nextActionWidthsByKey[action.action] = measuredWidth
    }

    return nextActionWidthsByKey[action.action] || 0
  })

  return {
    actionWidths,
    actionWidthsByKey: nextActionWidthsByKey,
  }
}

export function getRequestProfileHeaderFooterCachedActionWidths({
  actions,
  actionWidthsByKey,
}) {
  return actions.reduce((accumulator, action) => {
    if (actionWidthsByKey[action.action]) {
      accumulator[action.action] = actionWidthsByKey[action.action]
    }

    return accumulator
  }, {})
}
